import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    fetchNotificationsRequest,
    fetchNotificationCountRequest,
    markAsReadRequest,
    markAllAsReadRequest,
    deleteNotificationRequest,
    toggleDropdown,
    closeDropdown,
} from './slice';
import './NotificationIcon.css';

function NotificationIcon() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const dropdownRef = useRef(null);
    const { notifications, unreadCount, totalCount, loading, showDropdown } = useSelector((state) => state.notifications || {});

    // Fetch notification count on mount and periodically
    useEffect(() => {
        dispatch(fetchNotificationCountRequest());

        // Refresh count every 30 seconds
        const interval = setInterval(() => {
            dispatch(fetchNotificationCountRequest());
        }, 30000);

        return () => clearInterval(interval);
    }, [dispatch]);

    // Fetch notifications when dropdown opens
    useEffect(() => {
        if (showDropdown) {
            // Fetch all notifications (both read and unread) when dropdown opens
            dispatch(fetchNotificationsRequest({ page: 1, pageSize: 20 }));
        }
    }, [showDropdown, dispatch]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                dispatch(closeDropdown());
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown, dispatch]);

    const handleToggleDropdown = () => {
        dispatch(toggleDropdown());
    };

    const handleMarkAsRead = (id) => {
        dispatch(markAsReadRequest(id));
    };

    const handleMarkAllAsRead = () => {
        dispatch(markAllAsReadRequest());
    };

    const handleDeleteNotification = (e, id) => {
        e.stopPropagation(); // Prevent triggering mark as read
        dispatch(deleteNotificationRequest(id));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';

        // Parse date - backend sends UTC time without 'Z' suffix
        // Append 'Z' to ensure JavaScript treats it as UTC
        let utcDateString = dateString;
        if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
            utcDateString = dateString + 'Z';
        }
        const date = new Date(utcDateString);
        const now = new Date();

        // Calculate difference in milliseconds
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t('notifications.justNow');
        if (diffMins < 60) return t('notifications.minutesAgo', { count: diffMins });
        if (diffHours < 24) return t('notifications.hoursAgo', { count: diffHours });
        if (diffDays < 7) return t('notifications.daysAgo', { count: diffDays });

        // Format date and time in Vietnam timezone (UTC+7)
        return date.toLocaleString('vi-VN', {
            timeZone: 'Asia/Ho_Chi_Minh',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const formatNotificationMessage = (message) => {
        if (!message) return '';

        // Split message by newlines to handle multiple parts (e.g., main message + note)
        const lines = message.split('\n');
        const formattedLines = [];

        for (const line of lines) {
            if (!line.trim()) {
                formattedLines.push('');
                continue;
            }

            // Check if line contains translation key format {{Key}}|param1|param2
            const translationKeyMatch = line.match(/^\{\{([^}]+)\}\}(.*)$/);
            if (!translationKeyMatch) {
                formattedLines.push(line);
                continue;
            }

            const translationKey = translationKeyMatch[1];
            const paramsString = translationKeyMatch[2];

            // Parse parameters separated by |
            const params = paramsString.split('|').filter((p) => p.trim() !== '');

            // Map parameters based on translation key
            let translationParams = {};

            if (translationKey === 'NotificationNewDeviceCreatedMessage') {
                translationParams = {
                    deviceCode: params[0] || '',
                    deviceName: params[1] || '',
                };
            } else if (translationKey === 'NotificationDeviceApprovedMessage') {
                translationParams = {
                    deviceCode: params[0] || '',
                    deviceName: params[1] || '',
                };
            } else if (translationKey === 'NotificationDeviceRejectedMessage') {
                translationParams = {
                    deviceCode: params[0] || '',
                    deviceName: params[1] || '',
                };
            } else if (translationKey === 'NotificationDeviceStatusChangedMessage') {
                translationParams = {
                    deviceCode: params[0] || '',
                    oldStatus: params[1] || '',
                    newStatus: params[2] || '',
                };
            } else if (translationKey === 'NotificationDeviceLimitExceededMessage') {
                translationParams = {
                    unitName: params[0] || '',
                    approvedCount: params[1] || '',
                    maxDevices: params[2] || '',
                };
            } else if (translationKey === 'NotificationUserLimitExceededMessage') {
                translationParams = {
                    unitName: params[0] || '',
                    currentUsers: params[1] || '',
                    maxUsers: params[2] || '',
                };
            } else if (translationKey === 'NotificationGroupDeviceLimitExceededMessage') {
                translationParams = {
                    unitName: params[0] || '',
                    currentGroupDevices: params[1] || '',
                    maxGroupDevice: params[2] || '',
                };
            } else if (translationKey === 'NotificationNote') {
                translationParams = {
                    note: params[0] || '',
                };
            }

            // Get translated message
            let translatedMessage = t(translationKey, translationParams);

            // If translation returns the key itself, use original line
            if (translatedMessage === translationKey) {
                formattedLines.push(line);
            } else {
                formattedLines.push(translatedMessage);
            }
        }

        return formattedLines.join('\n');
    };

    const formatNotificationTitle = (title) => {
        if (!title) return '';

        // Check if title contains translation key format {{Key}}
        const translationKeyMatch = title.match(/^\{\{([^}]+)\}\}$/);
        if (!translationKeyMatch) return title;

        const translationKey = translationKeyMatch[1];
        const translated = t(translationKey);

        // If translation returns the key itself, return original
        return translated === translationKey ? title : translated;
    };

    return (
        <div className="notification-icon-container" ref={dropdownRef}>
            <button className="notification-icon-button" onClick={handleToggleDropdown} aria-label={t('notifications.title')}>
                <i className="fas fa-bell"></i>
                {unreadCount > 0 && <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
            </button>

            {showDropdown && (
                <div className="notification-dropdown">
                    <div className="notification-dropdown-header">
                        <h3>{t('notifications.title')}</h3>
                        {unreadCount > 0 && (
                            <button className="mark-all-read-button" onClick={handleMarkAllAsRead} title={t('notifications.markAllAsRead')}>
                                {t('notifications.markAllAsRead')}
                            </button>
                        )}
                    </div>

                    <div className="notification-dropdown-content">
                        {loading ? (
                            <div className="notification-loading">
                                <i className="fas fa-spinner fa-spin"></i>
                                <span>{t('notifications.loading')}</span>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="notification-empty">
                                <i className="fas fa-bell-slash"></i>
                                <span>{t('notifications.noNotifications')}</span>
                            </div>
                        ) : (
                            <div className="notification-list">
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                                        onClick={() => !notification.isRead && handleMarkAsRead(notification.id)}
                                    >
                                        <div className="notification-item-content">
                                            <div className="notification-title">{formatNotificationTitle(notification.title)}</div>
                                            <div className="notification-message">{formatNotificationMessage(notification.message)}</div>
                                            <div className="notification-time">{formatDate(notification.createdOn)}</div>
                                        </div>
                                        <div className="notification-item-actions">
                                            <button
                                                className="notification-delete-button"
                                                onClick={(e) => handleDeleteNotification(e, notification.id)}
                                                title={t('notifications.delete')}
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        </div>
                                        {!notification.isRead && <div className="notification-unread-indicator"></div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {notifications.length > 0 && (
                        <div className="notification-dropdown-footer">
                            <span>{t('notifications.total', { count: totalCount })}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default NotificationIcon;
